<template>
  <v-card class="elevation-12">
    <v-toolbar color="primary" dark>
      {{ $t('unitsPage.title') }}
    </v-toolbar>

    <v-data-table
      :items="units"
      :server-items-length="total"
      :headers="headers"
      hide-default-footer
    >
      <template v-slot:item.createdOn="{item}">
        {{ item.createdOn.format('DD.MM.YYYY HH:mm:ss') }}
      </template>

      <template v-slot:top>
        <v-sheet color="grey lighten-3" class="px-4 pt-4 pb-3" tile>
          <h3 class="transition-swing font-weight-medium">{{ $t('unitsPage.filterTitle') }}</h3>
          <v-responsive>
            <v-row>
              <v-col cols="12" sm="6" md="3" class="py-2">
                <v-autocomplete
                  v-model="filters.inn"
                  :items="organizations"
                  :label="$t('unitsPage.organization')"
                  :dense="true"
                  :clearable="true"
                  item-text="name"
                  item-value="inn"
                />
              </v-col>
              <v-spacer/>
              <v-col cols="12" sm="12" md="12" class="d-flex justify-space-between">
                <v-btn color="primary" @click="load">
                  {{ $t('unitsPage.refresh') }}
                  <v-icon>mdi-refresh</v-icon>
                </v-btn>
                <v-btn color="primary" @click="onAddUnitButtonClicked">
                  {{ $t('unitsPage.addUnit') }}
                </v-btn>

                <v-dialog v-model="addUnitDialog" :persistent="true" width="600px">
                  <AddGlobalUnitComponent
                    :organizations="organizations"
                    :allowed-regions="allowedRegions"
                    @cancel="onAddUnitCanceled"
                    @added="onAddUnitSuccess"
                  />
                </v-dialog>
              </v-col>
            </v-row>
          </v-responsive>
        </v-sheet>
      </template>

      <template v-slot:item.name="{item}">
        <template v-if="item.isAdmin">
          <v-badge color="error" :content="$t('unitsPage.admin')">
            {{ item.name }}
          </v-badge>
        </template>
        <template v-else>
          {{ item.name }}
        </template>
      </template>

      <template v-slot:item.actions="{item}">
        <v-btn icon @click="onEditUnitButtonClicked(item)">
          <v-icon>mdi-pencil</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <v-dialog v-model="editUnitDialog" :persistent="true" width="600px">
      <EditGlobalUnitComponent
        ref="editUnit"
        :allowed-regions="allowedRegions"
        @cancel="onEditUnitCanceled"
        @updated="onEditUnitSuccess"
      />
    </v-dialog>

    <v-snackbar
      v-model="isAddUnitSuccess"
      :timeout="5000"
      :top="true"
      color="success"
    >
      {{ $t('unitsPage.addSuccess') }}
    </v-snackbar>

    <v-snackbar
      v-model="isEditUnitSuccess"
      :timeout="5000"
      :top="true"
      color="success"
    >
      {{ $t('unitsPage.editSuccess') }}
    </v-snackbar>
  </v-card>
</template>

<script>
import {
  LAYOUT_LOADING_START,
  LAYOUT_LOADING_STOP,
  LAYOUT_TITLE_CHANGED
} from "@/store/modules/layout/actions";
import _ from "underscore";
import Mappers from "@/mappers";
import GlobalOrganizationService from "@/services/global.organization.service";
import GlobalUnitService from "@/services/global.unit.service";
import AddGlobalUnitComponent from "@/components/unit/AddGlobalUnitComponent.vue";
import EditGlobalUnitComponent from "@/components/unit/EditGlobalUnitComponent.vue";
import { API_HOST } from "@/settings/server";
import Vue from "vue";

export default {
  name: "UnitsPage",
  components: {
    AddGlobalUnitComponent,
    EditGlobalUnitComponent
  },
  data() {
    return {
      units: [],
      organizations: [],
      total: 0,
      filters: { inn: null },
      allowedRegions: [],
      addUnitDialog: false,
      editUnitDialog: false,
      isAddUnitSuccess: false,
      isEditUnitSuccess: false
    };
  },
  computed: {
    headers() {
      return [
        { text: this.$t('unitsPage.number'), align: 'center', sortable: false, value: 'displayNumber' },
        { text: this.$t('unitsPage.name'), align: 'left', sortable: true, value: 'name' },
        { text: this.$t('unitsPage.organization'), align: 'left', sortable: true, value: 'globalorganization.name' },
        { text: this.$t('unitsPage.inn'), align: 'center', sortable: true, value: 'globalorganization.inn' },
        { text: this.$t('unitsPage.address'), align: 'left', sortable: true, value: 'address' },
        { text: this.$t('unitsPage.registrationDate'), align: 'center', sortable: true, value: 'createdOn' },
        { text: '', align: 'right', sortable: false, value: 'actions' }
      ];
    }
  },
  methods: {
    load() {
      this.$store.dispatch(LAYOUT_LOADING_START);
      GlobalUnitService.getUnits({ inn: this.filters.inn })
        .then(response => {
          let displayNumber = 0;
          this.units = _.map(response.data, u => {
            let unit = Mappers.mapGlobalUnit(u);
            unit.displayNumber = ++displayNumber;
            return unit;
          });
          this.total = this.units.length;
        })
        .finally(() => {
          this.$store.dispatch(LAYOUT_LOADING_STOP);
        });
    },
    loadOrganizations() {
      this.$store.dispatch(LAYOUT_LOADING_START);
      GlobalOrganizationService.getOrganizations({})
        .then(response => {
          this.organizations = _.map(response.data, o => Mappers.mapGlobalOrganization(o?.Organization));
        })
        .finally(() => {
          this.load();
          this.$store.dispatch(LAYOUT_LOADING_STOP);
        });
    },
    loadAllowedRegions() {
      Vue.axios
        .get(`${API_HOST}/api/UnitRegion`, {
          headers: { Authorization: `Bearer ${this.$store.getters.accessToken}` }
        })
        .then(response => {
          this.allowedRegions = _.map(response.data, regionViewModel =>
            Mappers.mapRegion(regionViewModel)
          );
        })
        .catch(error => console.log(error));
    },
    onAddUnitButtonClicked() {
      this.addUnitDialog = true;
    },
    onAddUnitCanceled() {
      this.addUnitDialog = false;
    },
    onAddUnitSuccess() {
      this.isAddUnitSuccess = true;
      this.addUnitDialog = false;
      this.load();
    },
    onEditUnitButtonClicked(unit) {
      this.editUnitDialog = true;
      this.$nextTick(() => {
        this.$refs.editUnit.set(unit);
      });
    },
    onEditUnitCanceled() {
      this.editUnitDialog = false;
    },
    onEditUnitSuccess() {
      this.isEditUnitSuccess = true;
      this.editUnitDialog = false;
      this.load();
    }
  },
  mounted() {
    this.loadAllowedRegions();
  },
  created() {
    this.$store.dispatch(LAYOUT_TITLE_CHANGED, this.$t('unitsPage.title'));
    if (this.$route.query.inn)
      this.filters.inn = this.$route.query.inn;
    else
      this.filters.inn = this.$store.getters.appLogin.globalorganization.inn;

    this.loadOrganizations();
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        this.load();
      }
    }
  }
};
</script>

<style scoped>
</style>
